import React from 'react';
import { Link } from 'react-router-dom';

function DoctorNetwork() {
  return (
    <article className="seo-page-container" style={{ 
      padding: 'clamp(15px, 4vw, 30px)', 
      background: '#fff', 
      margin: 'clamp(10px, 3vw, 20px)', 
      borderRadius: '4px', 
      textAlign: 'left', 
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box'
    }}>
      
      {/* HEADER HERO SECTION */}
      <header>
        <h1 style={{ 
          color: '#0b3c5d', 
          marginBottom: '15px', 
          fontSize: 'clamp(1.5rem, 6vw, 2.4rem)', 
          fontWeight: '700', 
          lineHeight: '1.3' 
        }}>
          Global Doctor Networks: How to Research and Verify Specialized Surgeons Abroad
        </h1>
        <p style={{ color: '#444', lineHeight: '1.8', fontSize: 'clamp(0.95rem, 3.5vw, 1.1rem)', marginBottom: '25px' }}>
          When finalizing an itinerary for major overseas procedures—whether looking into complex cardiothoracic operations, intricate multi-joint orthopedic reconstructions, or advanced oncology care—the single most critical element to verify is the credential background of your operating specialist. 
          By utilizing open, independent research directories like <strong>Medical Trip World</strong>, international medical travelers can learn how to navigate global doctor registries, evaluate historical surgical logs, and verify clinical board certifications objectively before booking treatment.
        </p>
      </header>

      {/* CALL TO ACTION BUTTON */}
      <div style={{ marginBottom: '35px' }}>
        <Link to="/compare-costs" style={{ 
          background: '#28a745', 
          color: '#fff', 
          padding: '14px 24px', 
          textDecoration: 'none', 
          borderRadius: '4px', 
          fontWeight: 'bold', 
          display: 'inline-block', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '0.95rem',
          textAlign: 'center',
          boxSizing: 'border-box',
          maxWidth: '100%'
        }}>
          Access Independent Specialist Vetting Toolkits (Free) →
        </Link>
      </div>

      {/* SECTION 1: CRITICAL CORE VETTING CRITERIA */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Key Structural Frameworks for Independent Surgeon Auditing
        </h2>
        <p style={{ color: '#555', lineHeight: '1.7', marginBottom: '15px', fontSize: '0.95rem' }}>
          Evaluating a medical specialist across international borders requires looking past baseline hospital marketing. Traveling families should adopt a systematic approach to assess active surgeons by checking three foundational clinical pillars:
        </p>
        <ul style={{ paddingLeft: '20px', color: '#444', lineHeight: '1.8', fontSize: '0.95rem' }}>
          <li>
            <strong>Verified Postgraduate Track Records:</strong> Confirm whether the primary surgeon has completed extensive fellowships or post-graduate training tracks at recognized international boards or Western institutions (such as the Royal Colleges in the UK or major American surgical societies).
          </li>
          <li>
            <strong>Surgical Volume Intensity Metrics:</strong> Research the specialist’s historical performance frequency. High-volume operators who routinely complete 200+ specific procedures annually (like open-heart bypasses or complex knee arthroplasties) statistically show more consistent clinical outcomes.
          </li>
          <li>
            <strong>Peer-Reviewed Research Contributions:</strong> Check if the consultant actively participates in international clinical research or contributes to global peer-reviewed journals, which serves as a solid indicator of their involvement with modern surgical methodologies.
          </li>
        </ul>
      </section>

      {/* SECTION 2: SURGEON PROFILE STRUCTURE CHECKLIST */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Mandatory Specialist Assessment Logs
        </h2>
        <p style={{ color: '#555', lineHeight: '1.6', fontSize: '0.95rem' }}>
          When requesting a preliminary medical record evaluation, patients are advised to build an exhaustive research log containing these key data points:
        </p>

        <div style={{ background: '#f4f8fb', padding: 'clamp(15px, 4vw, 20px)', margin: '25px 0', borderRadius: '4px', borderLeft: '4px solid #005ba1', boxSizing: 'border-box' }}>
          <h3 style={{ marginTop: '0', color: '#005ba1', fontSize: '1.2rem', marginBottom: '10px' }}>📋 Specialist Verification Profile Fields:</h3>
          <ul style={{ marginTop: '10px', listStyleType: 'square', paddingLeft: '20px', lineHeight: '1.8', color: '#333', fontSize: '0.95rem' }}>
            <li><strong>Active National Registry Standing:</strong> Cross-reference the physician's licensing numbers directly against public national regulatory databases to guarantee their license is in good standing without restrictions.</li>
            <li><strong>Procedure-Specific Complication Rates:</strong> Request historical outcome data parameters or published track statistics related to your specific procedure to help verify structural clinic transparency.</li>
            <li><strong>Multidisciplinary Tumor/Care Boards:</strong> For complex cases like bone marrow transplants or oncology regimens, ensure your primary specialist coordinates with a collaborative team of pathologists, radiologists, and auxiliary therapists.</li>
            <li><strong>Cross-Border Language Commensurability:</strong> Confirm that your reviewing specialist speaks fluent English or provides professional medical translation staff to prevent any risk of miscommunicating sensitive symptoms.</li>
          </ul>
        </div>
      </section>

      {/* SECTION 3: SAFETY & ENCRYPTED CHANNELS - RESPONSIVE GRID */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Protecting Medical Privacy During Pre-Travel Tele-Consultations
        </h2>
        <p style={{ color: '#555', lineHeight: '1.6', fontSize: '0.95rem' }}>
          Because Medical Trip World operates strictly as an open informational venue and independent index, we remind traveling families to follow secure digital protocols when corresponding with international doctor wings. Always verify the following privacy baselines:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginTop: '15px' }}>
          <div style={{ padding: '15px', background: '#fafafa', borderRadius: '4px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#0b3c5d', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '6px' }}>🛡️ Encrypted File Repositories</h4>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              Ensure that all digital medical histories, CT/MRI scans, and laboratory panel readouts are transmitted via secure cloud architectures that align fully with global health data privacy standards.
            </p>
          </div>
          <div style={{ padding: '15px', background: '#fafafa', borderRadius: '4px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#0b3c5d', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '6px' }}>⚖️ Independent Verification Rights</h4>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              Patients maintain the fundamental right to independently cross-verify any clinical credentials or pricing estimations shared by foreign networks before formalizing an admission.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4: IN-DEPTH FAQ */}
      <section style={{ marginBottom: '35px', background: '#fff', border: '1px solid #e0e0e0', padding: 'clamp(15px, 4vw, 20px)', borderRadius: '4px' }}>
        <h3 style={{ color: '#0b3c5d', marginTop: '0', marginBottom: '20px', fontSize: '1.25rem' }}>
          Frequently Asked Questions (FAQs) – Doctor Directories & Vetting
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Are foreign surgeon credentials recognized by international healthcare boards?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            Yes. Leading medical tourism specialists frequently hold active memberships in global associations, such as the International Society of Orthopaedic Surgery or the World Heart Federation, which use standardized peer benchmarking systems.
          </p>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Can patients request a video call with their specific operating surgeon before traveling?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            Yes. Most internationally certified hospitals encourage remote pre-admission tele-consultations. This allows patients to discuss surgical approaches, review anesthetic risks, and clarify structural timeline expectations directly with their specialist.
          </p>
        </div>
        
        <div style={{ marginBottom: '5px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Does Medical Trip World employ these doctors or manage contractual networks?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            No. Medical Trip World functions exclusively as an independent informational directory and research portal. We do not manage surgical networks, employ medical specialists, or maintain formal corporate partnerships or Memorandums of Understanding (MOUs) with any hospital system. All financial contracts, clinical evaluations, and healthcare liabilities exist solely and directly between the patient and their selected medical professional.
          </p>
        </div>
      </section>

      {/* RE-ENGAGEMENT FOOTER */}
      <footer style={{ borderTop: '1px solid #eee', paddingTop: '20px', textAlign: 'center' }}>
        <p style={{ fontSize: '1.05rem', fontWeight: 'bold', color: '#333', margin: '0 0 15px 0' }}>Empower Your Global Healthcare Research Journey Safely</p>
        <div style={{ display: 'flex', gap: '12px', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
          <style>{`
            @media (max-width: 550px) {
              .footer-btn { width: 100% !important; text-align: center; box-sizing: border-box; }
            }
          `}</style>
          <Link to="/submit-query" className="footer-btn" style={{ background: '#28a745', color: '#fff', padding: '12px 20px', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', display: 'inline-block' }}>
            Submit General Information Request
          </Link>
          <Link to="/compare-costs" className="footer-btn" style={{ background: '#fff', color: '#0b3c5d', border: '2px solid #0b3c5d', padding: '10px 20px', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', display: 'inline-block' }}>
            View Estimated Cost Charts
          </Link>
        </div>
      </footer>

    </article>
  );
}

export default DoctorNetwork;